import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import useCachedPriceLevelsDictionaryController from "../../../../../../../hooks/use-price-level-dictionary";
import Api from "../../../../../../../../core/services/api/api";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Divider, MenuItem, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import Form, {MuiRFF, Yup} from "../../../../../../../components/form";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Button from "../../../../../../../components/base/button/button";
import SalesPersonAutocomplete from "../../../../../../../components/text-fields/autocomplete/sales-person-autocomplete";
import TerritoryAutocomplete from "../../../../../../../components/text-fields/autocomplete/territory-autocomplete";
import {deepEqual} from "../../../../../../../../core/services/utils/utils";

const emptyInitialValues = {
    salesPerson: null,
    territory: null,
    priceLevel: null,
    creditLimit: null,
    paymentTerm: null,
};

/**
 * Maps GET partner defaults + option lists into form initial values.
 *
 * @param {object|null|undefined} data
 * @param {Array} priceLevels
 * @param {Array} paymentTerms
 * @returns {typeof emptyInitialValues}
 */
const mapDefaultsToInitialValues = (data, priceLevels, paymentTerms) => {
    if (!data) {
        return {...emptyInitialValues};
    }
    return {
        salesPerson: data.salesPersonOperatorId
            ? {
                operatorId: data.salesPersonOperatorId,
                code: data.salesPersonCode,
                name: data.salesPersonName,
            }
            : null,
        territory: data.territoryCode
            ? {
                id: data.territoryCode,
                code: data.territoryCode,
                name: data.territoryName,
            }
            : null,
        priceLevel: priceLevels.find((pl) => pl.id === data.priceLevelId) ?? null,
        creditLimit: data.creditLimit ?? null,
        paymentTerm: paymentTerms.find((pt) => pt.code === data.paymentTerms) ?? null,
    };
};

/**
 * Maps form values to the API payload shape used for save (and dirty comparison).
 *
 * @param {typeof emptyInitialValues} values
 * @returns {{salesPersonOperatorId: string|null, territoryCode: string|null, priceLevelId: number|null, creditLimit: number|null, paymentTerms: string|null}}
 */
const mapValuesToApiPayload = (values) => ({
    salesPersonOperatorId: values?.salesPerson?.operatorId ?? null,
    territoryCode: values?.territory?.code ?? null,
    priceLevelId: values?.priceLevel?.id ?? null,
    creditLimit: values?.creditLimit == null || values.creditLimit === ''
        ? null
        : Number(values.creditLimit),
    paymentTerms: values?.paymentTerm?.code ?? null,
});

export const CustomerDefaultPropertiesConfigurationsSection = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settings.organizational.customer-default-properties'});
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [priceLevels, setPriceLevels] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [initialValues, setInitialValues] = useState(emptyInitialValues);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        salesPerson: Yup.mixed().nullable().required(ValidateMessages.required),
        territory: Yup.mixed().nullable().required(ValidateMessages.required),
        priceLevel: Yup.mixed().nullable().required(ValidateMessages.required),
        creditLimit: Yup.number()
            .nullable()
            .typeError(ValidateMessages.incorrect)
            .min(0, ValidateMessages.incorrect)
            .required(ValidateMessages.required),
        paymentTerm: Yup.mixed().nullable().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Loads partner defaults plus price-level and payment-term option lists.
     */
    const getConfigurations = useCallback(async () => {
        setLoading(true);
        const [defaultsResponse, priceLevelsResponse, paymentTermsResponse, priceLevelDict] = await Promise.all([
            Api.getPartnersSalesPersonAndTerritory(),
            Api.getAllPriceLevels(),
            Api.getAllPaymentTerms(),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted()) {
            return;
        }
        setLoading(false);

        const nextPriceLevels = (priceLevelsResponse?.resultFlag ? (priceLevelsResponse.data ?? []) : [])
            .map((e) => ({
                ...e,
                displayTitle: priceLevelDict?.find((dictionaryItem) => dictionaryItem.priceLevelId === e.id)?.title
                    ?? e.title
                    ?? '',
            }));
        const nextPaymentTerms = paymentTermsResponse?.resultFlag ? (paymentTermsResponse.data ?? []) : [];

        setPriceLevels(nextPriceLevels);
        setPaymentTerms(nextPaymentTerms);

        if (defaultsResponse?.resultFlag) {
            setInitialValues(mapDefaultsToInitialValues(defaultsResponse.data, nextPriceLevels, nextPaymentTerms));
        }
    }, [getPriceLevelDictionary, isMounted]);

    /**
     * Loads configurations on mount.
     */
    useEffect(() => {
        void getConfigurations();
    }, [getConfigurations]);

    /**
     * Saves all required new-customer default properties in one PUT.
     *
     * @param {typeof emptyInitialValues} values
     * @param {import('final-form').FormApi} form
     */
    const onSubmit = useCallback(async (values, form) => {
        const forApi = mapValuesToApiPayload(values);
        if (deepEqual(forApi, mapValuesToApiPayload(initialValues))) {
            return {resultFlag: true};
        }
        setSubmitting(true);
        const response = await Api.updatePartnersSalesPersonAndTerritory(forApi);
        if (!isMounted()) {
            return;
        }
        setSubmitting(false);
        if (response?.resultFlag) {
            const nextInitialValues = {
                salesPerson: values.salesPerson,
                territory: values.territory,
                priceLevel: values.priceLevel,
                creditLimit: forApi.creditLimit,
                paymentTerm: values.paymentTerm,
            };
            setInitialValues(nextInitialValues);
            form.restart(nextInitialValues);
        }
        return response;
    }, [initialValues, isMounted]);

    if (loading) {
        return (
            <Grid2 xs={12}>
                <div className={'loading access-level w-100'}>
                    <div>
                        <div/>
                    </div>
                </div>
            </Grid2>
        );
    }

    return (
        <Grid2 xs={12}>
            <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'customer-default-properties-form'}}
                >
                    {({values}) => {
                        const hasChanges = !deepEqual(
                            mapValuesToApiPayload(values),
                            mapValuesToApiPayload(initialValues),
                        );
                        return (
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        {t('title')}
                                    </Typography>
                                    <Typography variant={'body2'} color={'text.secondary'} sx={{mt: 0.5}}>
                                        {t('description')}
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>{t('salesPersonLabel')}</Typography>
                                        <Tooltip title={t('salesPersonTooltip')}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <SalesPersonAutocomplete
                                        name="salesPerson"
                                        form
                                        required={required.salesPerson}
                                        fullWidth
                                        disabled={submitting}
                                        textFieldProps={{
                                            placeholder: t('salesPersonPlaceholder'),
                                        }}
                                    />
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>{t('territoryLabel')}</Typography>
                                        <Tooltip title={t('territoryTooltip')}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <TerritoryAutocomplete
                                        name="territory"
                                        form
                                        required={required.territory}
                                        fullWidth
                                        disabled={submitting}
                                        textFieldProps={{
                                            placeholder: t('territoryPlaceholder'),
                                        }}
                                    />
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>{t('priceLevelLabel')}</Typography>
                                        <Tooltip title={t('priceLevelTooltip')}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        select
                                        name="priceLevel"
                                        required={required.priceLevel}
                                        fullWidth
                                        disabled={submitting || priceLevels.length === 0}
                                        SelectProps={{
                                            renderValue: (value) => value?.displayTitle ?? '',
                                        }}
                                    >
                                        {priceLevels.map((priceLevel) => (
                                            <MenuItem key={priceLevel.id} value={priceLevel}>
                                                {priceLevel.displayTitle}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>{t('paymentTermLabel')}</Typography>
                                        <Tooltip title={t('paymentTermTooltip')}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        select
                                        name="paymentTerm"
                                        required={required.paymentTerm}
                                        fullWidth
                                        disabled={submitting || paymentTerms.length === 0}
                                        SelectProps={{
                                            renderValue: (value) => value
                                                ? `${value.description} (${value.code})`
                                                : '',
                                        }}
                                    >
                                        {paymentTerms.map((paymentTerm) => (
                                            <MenuItem key={paymentTerm.id ?? paymentTerm.code} value={paymentTerm}>
                                                {paymentTerm.description} ({paymentTerm.code})
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>

                                <Grid2 xs={12} lg={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                        <Typography variant="body2" fontWeight={500}>{t('creditLimitLabel')}</Typography>
                                        <Tooltip title={t('creditLimitTooltip')}>
                                            <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                        </Tooltip>
                                    </Box>
                                    <MuiRFF.TextField
                                        name="creditLimit"
                                        type="number"
                                        required={required.creditLimit}
                                        fullWidth
                                        disabled={submitting}
                                        InputProps={{
                                            inputProps: {min: 0},
                                        }}
                                    />
                                </Grid2>

                                <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={submitting || !hasChanges}
                                        form="customer-default-properties-form"
                                        sx={{minWidth: 200}}
                                    >
                                        {submitting ? t('saving') : t('save')}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        );
                    }}
                </Form>
            </Card>
        </Grid2>
    );
};
